import React, { useMemo, useState } from 'react'
import { graphql, useFragment } from 'react-relay'

import { XStack, YStack, isWeb } from 'tamagui'

import { lazy as loadable } from '@loadable/component'

import {
  Paragraph,
  Icon,
  Text,
  Card,
  EventAttendButton,
  EventAttendeesOverlap,
  Dialog,
} from '@newComponents'

import { wrapSuspenseDialog } from '@newComponents/SuspenseWrapper'

import { differenceInCalendarDays } from 'date-fns'

import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import {
  EventScreenAttendCard_event$key,
  EventScreenAttendCard_event$data,
} from './__generated__/EventScreenAttendCard_event.graphql'
import {
  EventScreenAttendCard_query$key,
  EventScreenAttendCard_query$data,
} from './__generated__/EventScreenAttendCard_query.graphql'

const EventScreenAttendeesDialog = wrapSuspenseDialog(
  loadable(() => import('./EventScreenAttendeesDialog'))
)

const MIN_ATTENDEES_TO_RENDER = 0

export const EventScreenAttendCard = ({
  event,
  query,
}: {
  event: EventScreenAttendCard_event$key
  query: EventScreenAttendCard_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenAttendCard_event on Event {
        slug
        startAt
        endAt

        eventAttendeesByRelevance(
          filter: {
            status: {
              in: [
                ATTENDING_IN_PERSON
                ATTENDING_VIRTUAL
                WAITING_IN_PERSON
                WAITING_VIRTUAL
              ]
            }
          }
          first: 8
        ) {
          totalCount
          ...EventAttendeesOverlap_eventAttendees
        }

        attendingCount: eventAttendees(
          filter: { status: { in: [ATTENDING_IN_PERSON, ATTENDING_VIRTUAL] } }
        ) {
          totalCount
        }

        waitingCount: eventAttendees(
          filter: { status: { in: [WAITING_IN_PERSON, WAITING_VIRTUAL] } }
        ) {
          totalCount
        }

        owner {
          __typename

          ... on User {
            isMe
          }

          ... on Guild {
            myMembership {
              membershipLevel
            }
          }
        }

        myAttendance {
          status

          ticketPurchase {
            status
          }
        }

        eventTicketingTiers {
          totalCount
        }

        ...EventAttendButtonNew_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventScreenAttendCard_query on Query {
        ...EventAttendButtonNew_query

        viewer {
          authorizationLevel
        }
      }
    `,
    query
  )

  return (
    <Card gap='$4' borderRadius='$8' padding='$4' paddingBottom={14}>
      <EventScreenAttendCardHeader data={data} />

      <XStack
        gap='$4'
        paddingTop='$1'
        // display='none'
        // $gtLg={{ display: 'flex' }}
      >
        <EventScreenAttendeesDialogTrigger data={data} queryData={queryData} />

        <EventAttendButton
          event={data}
          query={queryData}
          height={32}
          minHeight={32}
          borderRadius={16}
        />
      </XStack>
    </Card>
  )
}

const EventScreenAttendCardHeader = ({
  data,
}: {
  data: EventScreenAttendCard_event$data
}) => {
  const daysToGo = useMemo(() => {
    return differenceInCalendarDays(new Date(data.startAt), new Date())
  }, [data.startAt])

  const eventInPast = useMemo(() => {
    return new Date().getTime() > new Date(data.endAt).getTime()
  }, [data.endAt])

  if (!data.myAttendance || data.myAttendance.status === 'NOT_ATTENDING') {
    if (eventInPast) {
      return (
        <Paragraph size='$4' paddingTop='$1'>
          We missed you this time around!
        </Paragraph>
      )
    }

    return (
      <>
        <XStack gap='$2' alignItems='center' paddingTop='$1'>
          <EmojiIcon size='$5' name='waving-hand' />
          <Paragraph size='$4'>Ready to join in on the fun?</Paragraph>
        </XStack>

        {data.eventTicketingTiers.totalCount > 0 &&
          data.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED' && (
            <Paragraph size='$4'>Grab a ticket to secure your spot</Paragraph>
          )}

        {data.myAttendance?.status === 'NOT_ATTENDING' && (
          <Paragraph size='$4'>
            You're not attending, and won't receive notifications for this
            event.
          </Paragraph>
        )}
      </>
    )
  }

  if (
    ['WAITING_IN_PERSON', 'WAITING_VIRTUAL'].includes(data.myAttendance.status)
  ) {
    if (eventInPast) {
      return (
        <Paragraph size='$4' paddingTop='$1'>
          We missed you this time around!
        </Paragraph>
      )
    }

    if (
      data.eventTicketingTiers.totalCount &&
      data.myAttendance.ticketPurchase?.status !== 'SUCCEEDED'
    ) {
      return (
        <>
          <XStack gap='$2' alignItems='center' paddingTop='$1'>
            <EmojiIcon size='$5' name='waving-hand' />
            <Paragraph size='$4'>Ready to join in on the fun?</Paragraph>
          </XStack>

          <Paragraph size='$4'>Grab a ticket to secure your spot</Paragraph>
        </>
      )
    }

    return (
      <>
        <Paragraph size='$4' paddingTop='$1'>
          You're waiting for a spot to become available.
        </Paragraph>

        <Paragraph size='$4'>We'll email you if you get one!</Paragraph>
      </>
    )
  }

  if (daysToGo > 0) {
    return (
      <>
        <XStack gap='$1' alignItems='center' paddingTop='$1'>
          <Paragraph size='$4'>You're attending!</Paragraph>

          <EmojiIcon size='$4' name='party-popper' />
        </XStack>

        <XStack gap='$1' alignItems='center'>
          <Paragraph size='$4'>
            Only {daysToGo} more {daysToGo === 1 ? 'day' : 'days'} to go
          </Paragraph>

          <EmojiIcon size='$4' name='raising-hands' />
        </XStack>
      </>
    )
  }

  if (daysToGo === 0) {
    return (
      <>
        <XStack gap='$1' alignItems='center' paddingTop='$1'>
          <Paragraph size='$4'>You're attending!</Paragraph>

          <EmojiIcon size='$4' name='party-popper' />
        </XStack>

        <XStack gap='$1' alignItems='center'>
          <Paragraph size='$4'>Today is the day</Paragraph>

          <EmojiIcon size='$4' name='raising-hands' />
        </XStack>
      </>
    )
  }

  return (
    <>
      <XStack gap='$1' alignItems='center' paddingTop='$1'>
        <Paragraph size='$4'>You attended!</Paragraph>

        <EmojiIcon size='$4' name='party-popper' />
      </XStack>

      <XStack gap='$1' alignItems='center'>
        <Paragraph size='$4'>We look forward to seeing you next time</Paragraph>

        <EmojiIcon size='$4' name='waving-hand' />
      </XStack>
    </>
  )
}

const EventScreenAttendeesDialogTrigger = ({
  data,
  queryData,
}: {
  data: EventScreenAttendCard_event$data
  queryData: EventScreenAttendCard_query$data
}) => {
  const [attendeesWidth, setAttendeesWidth] = useState(200)

  const limitAttendees = useMemo(() => {
    const singleAttendeeWidth = 28

    const computedWidth = (attendeesWidth - singleAttendeeWidth) * (3 / 2)

    return Math.max(Math.floor(computedWidth / singleAttendeeWidth), 1)
  }, [attendeesWidth])

  if (
    queryData.viewer?.authorizationLevel === 'ADMIN' ||
    (data.owner?.__typename === 'Guild' &&
      ['ORGANIZER', 'EDITOR'].includes(
        data.owner?.myMembership?.membershipLevel || ''
      )) ||
    (data.owner?.__typename === 'User' && data.owner.isMe) ||
    (data.eventTicketingTiers.totalCount === 0 &&
      data.eventAttendeesByRelevance.totalCount > MIN_ATTENDEES_TO_RENDER)
  ) {
    return (
      <Dialog modal>
        <Dialog.Trigger tag='dialog' flex={1}>
          <XStack
            onLayout={(event) => {
              setAttendeesWidth(event.nativeEvent.layout.width)
            }}
            flex={1}
            gap='$4'
            alignItems='center'
            cursor='pointer'
            // onHoverIn={() => {
            //   // TODO: Swap attendee text with hover instructions
            // }}
            // onHoverOut={() => {
            //   // TODO: Swap attendee text with hover instructions
            // }}
          >
            {!!data.eventAttendeesByRelevance.totalCount && (
              <EventAttendeesOverlap
                size='$7'
                attendingCount={data.attendingCount.totalCount}
                waitingCount={data.waitingCount.totalCount}
                eventAttendees={data.eventAttendeesByRelevance}
                limitAttendees={limitAttendees}
              />
            )}
          </XStack>
        </Dialog.Trigger>

        <Dialog.Adapt when='xs'>
          <Dialog.Sheet key='sheet'>
            <Dialog.Sheet.Handle zIndex={200_000} />
            <Dialog.Sheet.Frame
              padding={0}
              gap='$4'
              shadowColor='$grey6'
              shadowRadius='$2'
              borderTopRightRadius='$6'
              borderTopLeftRadius='$6'
            >
              <Dialog.Sheet.ScrollView
                // borderTopRightRadius='$6'
                // borderTopLeftRadius='$6'
                paddingTop='$6'
                paddingHorizontal='$6'
                contentContainerStyle={{ flex: 1 }}
              >
                <YStack gap='$4'>
                  {isWeb ? (
                    <EventScreenAttendeesDialog slug={data.slug} />
                  ) : (
                    <Dialog.Adapt.Contents />
                  )}
                </YStack>
              </Dialog.Sheet.ScrollView>
            </Dialog.Sheet.Frame>
          </Dialog.Sheet>
        </Dialog.Adapt>

        <Dialog.Portal>
          <Dialog.Content
            elevate
            key='content'
            gap='$4'
            minWidth={400}
            maxWidth={600}
            minHeight={400}
            maxHeight={600}
            forceMount
          >
            <EventScreenAttendeesDialog slug={data.slug} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    )
  }

  return <XStack flex={1} gap='$4' alignItems='center' />
}
